import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { adminApi } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Search, Edit3, Trash2, Eye, EyeOff, Loader2 } from "lucide-react";

/**
 * Generic list page for a CMS collection.
 * columns: [{ key, label, render? }]
 * Renders a table on md+ and card list on mobile.
 */
export default function AdminList({
  title, kicker, resource, createTo, editTo, columns,
  emptyMessage = "Nenhum item cadastrado.",
  testId,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [deleting, setDeleting] = useState(null);

  const load = async (query = "") => {
    setLoading(true);
    try {
      const { data } = await adminApi.get(`/admin/${resource}`, { params: query ? { q: query } : {} });
      setItems(data);
    } catch { toast.error("Falha ao carregar"); }
    setLoading(false);
  };

  useEffect(() => { load(); }, [resource]);

  const remove = async (id) => {
    if (!window.confirm("Excluir este item? Esta ação é irreversível.")) return;
    setDeleting(id);
    try {
      await adminApi.delete(`/admin/${resource}/${id}`);
      toast.success("Excluído");
      setItems((x) => x.filter((i) => i.id !== id));
    } catch { toast.error("Falha ao excluir"); }
    setDeleting(null);
  };

  const togglePublish = async (item) => {
    try {
      await adminApi.patch(`/admin/${resource}/${item.id}/publish`, null, { params: { published: !item.published } });
      setItems((x) => x.map((i) => i.id === item.id ? { ...i, published: !i.published } : i));
      toast.success(item.published ? "Despublicado" : "Publicado");
    } catch { toast.error("Falha ao alterar"); }
  };

  const renderActions = (it) => (
    <div className="flex items-center gap-2 justify-end">
      <button
        onClick={() => togglePublish(it)}
        title={it.published ? "Despublicar" : "Publicar"}
        className={`w-9 h-9 grid place-items-center rounded-full border ${it.published ? "border-[#C6A762]/40 text-[#C6A762]" : "border-[#2a2a2a] text-[#7d7d7d]"} hover:bg-white/5`}
      >
        {it.published ? <Eye size={14} /> : <EyeOff size={14} />}
      </button>
      <Link to={`${editTo}/${it.id}`} className="w-9 h-9 grid place-items-center rounded-full border border-[#2a2a2a] text-[#B8B8B8] hover:text-white hover:border-[#C6A762]/40">
        <Edit3 size={14} />
      </Link>
      <button
        onClick={() => remove(it.id)}
        disabled={deleting === it.id}
        className="w-9 h-9 grid place-items-center rounded-full border border-[#2a2a2a] text-[#B8B8B8] hover:text-red-400 hover:border-red-500/40 disabled:opacity-40"
      >
        {deleting === it.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
      </button>
    </div>
  );

  return (
    <div className="space-y-6 sm:space-y-8" data-testid={testId}>
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-3 text-[#C6A762] mb-3">
            <span className="gold-divider" />
            <span className="text-[11px] tracking-[0.35em] uppercase">{kicker}</span>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl">{title}</h1>
        </div>
        <Link to={createTo} data-testid={`${resource}-create-btn`} className="btn-gold px-5 py-3 rounded-full text-xs inline-flex items-center gap-2">
          <Plus size={14} /> Novo
        </Link>
      </div>

      <div className="relative w-full sm:max-w-md">
        <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#7d7d7d]" />
        <input
          data-testid={`${resource}-search`}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load(q)}
          placeholder="Buscar por nome ou slug…"
          className="input-rotta pl-10"
        />
      </div>

      {/* Desktop: table */}
      <div className="card-rotta overflow-x-auto hidden md:block">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-[#141414] text-[10px] tracking-[0.25em] uppercase text-[#7d7d7d]">
            <tr>
              {columns.map((c) => <th key={c.key} className="text-left px-5 py-4 font-medium">{c.label}</th>)}
              <th className="px-5 py-4 text-right font-medium">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={columns.length + 1} className="py-14 text-center text-[#7d7d7d]"><Loader2 className="inline animate-spin" size={16} /></td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={columns.length + 1} className="py-14 text-center text-[#7d7d7d]">{emptyMessage}</td></tr>
            ) : items.map((it) => (
              <tr key={it.id} className="border-t border-[#1a1a1a] hover:bg-white/[0.02]" data-testid={`${resource}-row-${it.id}`}>
                {columns.map((c) => (
                  <td key={c.key} className="px-5 py-4 text-white/90 align-middle">
                    {c.render ? c.render(it) : (it[c.key] ?? "—")}
                  </td>
                ))}
                <td className="px-5 py-4">
                  {renderActions(it)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile: cards */}
      <div className="md:hidden space-y-3">
        {loading ? (
          <div className="card-rotta p-8 text-center text-[#7d7d7d]"><Loader2 className="inline animate-spin" size={16} /></div>
        ) : items.length === 0 ? (
          <div className="card-rotta p-8 text-center text-[#7d7d7d] text-sm">{emptyMessage}</div>
        ) : items.map((it) => (
          <div key={it.id} data-testid={`${resource}-card-${it.id}`} className="card-rotta p-4">
            <div className="mb-3">
              {columns[0].render ? columns[0].render(it) : <div className="text-white font-medium">{it[columns[0].key] ?? "—"}</div>}
            </div>
            {columns.length > 1 && (
              <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-xs border-t border-[#1a1a1a] pt-3">
                {columns.slice(1).map((c) => (
                  <div key={c.key} className="min-w-0">
                    <dt className="text-[9px] tracking-[0.2em] uppercase text-[#7d7d7d]">{c.label}</dt>
                    <dd className="mt-0.5 text-white/85 truncate">
                      {c.render ? c.render(it) : (it[c.key] ?? "—")}
                    </dd>
                  </div>
                ))}
              </dl>
            )}
            <div className="pt-3 mt-3 border-t border-[#1a1a1a]">
              {renderActions(it)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
